<template>
    <div class="groups-view ma-4">
        <v-data-table v-if="userInvitations.length > 0"
                      hover
                      class="mb-4"
                      width="auto"
                      :headers="getInvitationsHeaders()"
                      :items="userInvitations">
            <template v-slot:item.rawItem="{ value }">
                <v-btn text="Подробности"
                       @click="showInvitation(value)"
                       color="primary"
                       variant="plain" />
            </template>
        </v-data-table>

        <v-btn class="mb-4"
               @click="createGroup"
               text="Создать группу"
               color="lonely"
               variant="flat" />

        <v-data-table hover
                      width="auto"
                      :headers="getGroupsHeaders()"
                      :items="groups">
            <template v-slot:item.rawItem1="{ value }">
                <v-btn variant="plain"
                       class="group-name px-0"
                       :text="value.name"
                       :to="{ name: 'groupInfoPage', params: { groupGuid: value.guid }}" />
            </template>
            <template v-slot:item.rawItem2="{ value }">
                <v-icon icon="mdi-pencil-circle-outline"
                        title="Редактировать"
                        :class="getEditButtonClasses(value)"
                        @click="editGroup(value)" />
            </template>
        </v-data-table>

        <GroupDialog v-if="showGroupDialog"
                     @hide="hideGroupDialog"
                     :group="editedGroup"
                     edit-title="Редактирование группы"
                     create-title="Создание группы"
                     @update:group="upsertServerGroup($event)" />

        <v-dialog v-model="showInvitationDialog"
                  min-width="300"
                  width="auto"
                  transition="dialog-bottom-transition">
            <v-card class="text-primary ma-0">
                <v-card-title>
                    {{selectedInvitation?.title}}
                </v-card-title>

                <v-card-text class="pa-4 py-0">
                    <InvitationInfoList :invitation="selectedInvitation" />
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="secondary"
                           variant="elevated"
                           @click="showInvitationDialog = false">
                        Отменить
                    </v-btn>
                    <v-btn color="error"
                           variant="elevated"
                           @click="respond(false)">
                        Отклонить приглашение
                    </v-btn>
                    <v-btn color="primary"
                           variant="elevated"
                           @click="respond(true)">
                        Принять приглашение
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
    import { mapState } from "vuex";
    import { DateTime } from "luxon";

    import GroupDialog from "@/components/GroupDialog.vue";
    import InvitationInfoList from "@/components/InvitationInfoList.vue";

    export default {
        components: {
            GroupDialog,
            InvitationInfoList
        },

        data() {
            return {
                showGroupDialog: false,
                editedGroup: null,

                showInvitationDialog: false,
                selectedInvitation: null
            };
        },

        created() {
            this.$store.dispatch("loadUserGroups");
            this.$store.dispatch("loadUserInvitations");
            this.$store.dispatch("loadSentInvitations");
        },

        computed: {
            ...mapState(["currentLocale"]),

            groups() {
                return this.$store.state.userGroups
                    .map(e => ({
                        guid: e.guid,
                        name: e.name,
                        description: e.description,
                        membersCount: e.membersCount,
                        createdDate: this.getDateTime(e.createdDate),
                        creatorName: e.creator.userName,
                        rawItem1: e,
                        rawItem2: e
                    }));
            },

            userInvitations() {
                return this.$store.state.userInvitations
                    .map(e => ({
                        date: this.getDateTime(e.dateTime),
                        title: e.title,
                        senderName: e.senderName,
                        rawItem: e
                    }));
            }
        },

        watch: {

        },

        methods: {
            showInvitation(invitation) {
                this.selectedInvitation = invitation;
                this.showInvitationDialog = true;
            },

            respond(accept) {
                const request = {
                    invitationId: this.selectedInvitation.id,
                    acceptInvitation: accept
                };

                this.$store.dispatch("respondToInvitation", request)
                    .then(() => {
                        this.$store.dispatch("loadUserGroups");
                        this.$store.dispatch("loadUserInvitations");
                    });

                this.selectedInvitation = null;
                this.showInvitationDialog = false;
            },

            createGroup() {
                this.showGroupDialog = true;
                this.editedGroup = null;
            },

            editGroup(group) {
                if (!this.isCreator(group)) {
                    return;
                }

                this.showGroupDialog = true;
                this.editedGroup = group;
            },

            hideGroupDialog() {
                this.showGroupDialog = false;
                this.editedGroup = null;
            },

            upsertServerGroup(group) {
                this.$store.dispatch("upsertGroup", group)
                    .then(() => {
                        this.$store.dispatch("loadUserGroups");
                    });
            },

            isCreator(group) {
                return window.AcademIt.UserId === group.creator.id;
            },

            getEditButtonClasses(group) {
                if (this.isCreator(group)) {
                    return "text-primary";
                }

                return "text-grey-lighten-1 disabled";
            },

            getDateTime(isoDateTime) {
                return DateTime.fromISO(isoDateTime).setLocale(this.currentLocale).toFormat("yyyy.MM.dd HH:mm");
            },

            getInvitationsHeaders() {
                return [
                    {
                        title: "Дата создания",
                        align: "start",
                        sortable: true,
                        key: "date"
                    },
                    {
                        title: "Заголовок приглашения",
                        align: "start",
                        sortable: true,
                        key: "title"
                    },
                    {
                        title: "Имя приглашающего",
                        align: "start",
                        sortable: true,
                        key: "senderName"
                    },
                    {
                        title: "",
                        align: "end",
                        sortable: false,
                        key: "rawItem"
                    }
                ];
            },

            getGroupsHeaders() {
                return [
                    {
                        title: "Название",
                        align: "start",
                        sortable: true,
                        sort: (e1, e2) => e1.name === e2.name
                            ? 0
                            : e1.name > e2.name
                                ? 1
                                : -1,
                        key: "rawItem1"
                    },
                    {
                        title: "Описание",
                        align: "start",
                        sortable: true,
                        key: "description"
                    },
                    {
                        title: "Количество участников",
                        align: "end",
                        sortable: true,
                        key: "membersCount"
                    },
                    {
                        title: "Дата создания",
                        align: "start",
                        sortable: true,
                        key: "createdDate"
                    },
                    {
                        title: "Имя создателя",
                        align: "start",
                        sortable: true,
                        key: "creatorName"
                    },
                    {
                        title: "",
                        align: "end",
                        sortable: false,
                        key: "rawItem2"
                    }
                ];
            }
        }
    };
</script>

<style lang="scss">
    .groups-view .disabled {
        cursor: default;
    }

    .groups-view .group-name,
    .groups-view .v-data-table-header__content,
    .groups-view .v-data-table-footer {
        color: rgb(var(--v-theme-primary)) !important;
        font-weight: bold;
    }
</style>